import { useQuery } from "@tanstack/react-query";
import { Users, Building2, DollarSign, TrendingUp } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/page-header";
import { Link } from "wouter";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

interface DashboardStats {
  contactCount: number;
  accountCount: number;
  activeDealCount: number;
  totalDealValue: number;
  estimatedCommission: number;
  pipelineByStage: Array<{
    stage: string;
    count: number;
    value: number;
  }>;
}

export default function Dashboard() {
  // Fetch summary statistics
  const { data: stats, isLoading } = useQuery<DashboardStats>({
    queryKey: ["/api/dashboard/stats"],
  });

  const pipelineData = stats?.pipelineByStage || [];

  return (
    <div className="flex h-screen w-full flex-col">
      <PageHeader
        title="Dashboard"
        breadcrumbs={[{ label: "Dashboard" }]}
      />

      <main className="flex-1 overflow-auto bg-muted/20 p-6">
        <div className="mx-auto max-w-7xl space-y-6">
          {/* Summary Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card className="hover-elevate" data-testid="card-contacts-summary">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Contacts</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="text-2xl font-bold text-muted-foreground">-</div>
                ) : (
                  <div className="text-2xl font-bold">{stats?.contactCount || 0}</div>
                )}
                <p className="text-xs text-muted-foreground">
                  Total contacts in CRM
                </p>
              </CardContent>
            </Card>

            <Card className="hover-elevate" data-testid="card-accounts-summary">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Accounts</CardTitle>
                <Building2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="text-2xl font-bold text-muted-foreground">-</div>
                ) : (
                  <div className="text-2xl font-bold">{stats?.accountCount || 0}</div>
                )}
                <p className="text-xs text-muted-foreground">
                  Business entities
                </p>
              </CardContent>
            </Card>

            <Card className="hover-elevate" data-testid="card-deals-summary">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Deals</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="text-2xl font-bold text-muted-foreground">-</div>
                ) : (
                  <div className="text-2xl font-bold">{stats?.activeDealCount || 0}</div>
                )}
                <p className="text-xs text-muted-foreground">
                  {isLoading ? 'Total value: -' : (() => {
                    const value = stats?.totalDealValue || 0;
                    if (value >= 1000000) {
                      return `Total value: $${(value / 1000000).toFixed(2)}M`;
                    } else if (value >= 1000) {
                      return `Total value: $${(value / 1000).toFixed(1)}k`;
                    } else {
                      return `Total value: $${value.toLocaleString()}`;
                    }
                  })()}
                </p>
              </CardContent>
            </Card>

            <Card className="hover-elevate" data-testid="card-commission-summary">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Est. Commission</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="text-2xl font-bold text-muted-foreground">-</div>
                ) : (() => {
                    const commission = stats?.estimatedCommission || 0;
                    if (commission >= 1000000) {
                      return <div className="text-2xl font-bold">${(commission / 1000000).toFixed(2)}M</div>;
                    } else if (commission >= 1000) {
                      return <div className="text-2xl font-bold">${(commission / 1000).toFixed(1)}k</div>;
                    } else {
                      return <div className="text-2xl font-bold">${commission.toLocaleString()}</div>;
                    }
                  })()}
                <p className="text-xs text-muted-foreground">
                  From active pipeline
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Pipeline Chart */}
          <Card data-testid="card-pipeline-chart">
            <CardHeader>
              <CardTitle>Deal Pipeline</CardTitle>
              <CardDescription>Deals by stage with total value</CardDescription>
            </CardHeader>
            <CardContent className="h-80">
              {isLoading ? (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  Loading pipeline data...
                </div>
              ) : pipelineData.length === 0 || pipelineData.every(d => d.count === 0) ? (
                <div className="flex h-full flex-col items-center justify-center text-muted-foreground">
                  <p className="text-sm">No deals in pipeline yet</p>
                  <Link href="/deals" className="mt-2">
                    <Button variant="outline" size="sm" data-testid="button-create-first-deal">
                      <DollarSign className="mr-2 h-4 w-4" />
                      Create your first deal
                    </Button>
                  </Link>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pipelineData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis dataKey="stage" type="category" width={100} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "0.5rem",
                      }}
                    />
                    <Legend />
                    <Bar dataKey="count" fill="hsl(var(--primary))" name="Deal Count" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card data-testid="card-quick-actions">
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>Common tasks to get started</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
              <Link href="/contacts">
                <Button variant="outline" className="w-full justify-start" data-testid="button-quick-add-contact">
                  <Users className="mr-2 h-4 w-4" />
                  Add Contact
                </Button>
              </Link>
              <Link href="/accounts">
                <Button variant="outline" className="w-full justify-start" data-testid="button-quick-add-account">
                  <Building2 className="mr-2 h-4 w-4" />
                  Add Account
                </Button>
              </Link>
              <Link href="/deals">
                <Button variant="outline" className="w-full justify-start" data-testid="button-quick-add-deal">
                  <DollarSign className="mr-2 h-4 w-4" />
                  Create Deal
                </Button>
              </Link>
              <Link href="/scenarios">
                <Button variant="outline" className="w-full justify-start" data-testid="button-quick-capture-scenario">
                  <TrendingUp className="mr-2 h-4 w-4" />
                  Capture Scenario
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
